import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Menu, MessageSquare, User, LogOut, Plus } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

const links = [
  { to: "/listings", label: "Browse" },
  { to: "/removal-companies", label: "Removal Firms" },
  { to: "/briefs", label: "Clearance Requests" },
  { to: "/sell", label: "Sell / Clear" },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <header className="sticky top-0 z-50 border-b border-border bg-white/85 backdrop-blur-xl">
      <div className="mx-auto max-w-7xl px-6 lg:px-10 h-16 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2 group">
          <div className="w-8 h-8 bg-forest text-white grid place-items-center font-display font-black text-lg leading-none">FX</div>
          <div className="leading-none">
            <div className="font-display font-black tracking-tight text-lg text-forest">Furniture Exchange</div>
            <div className="overline text-[10px] text-muted-foreground -mt-0.5">UK · office clearance market</div>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
              className={({ isActive }) =>
                `text-sm transition-colors duration-200 ${isActive ? "text-forest font-medium" : "text-muted-foreground hover:text-forest"}`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          {user ? (
            <>
              <Button
                variant="outline"
                onClick={() => navigate("/dashboard/messages")}
                data-testid="nav-messages-btn"
                className="rounded-none"
              >
                <MessageSquare className="w-4 h-4 mr-2" /> Inbox
              </Button>
              <Button
                onClick={() => navigate("/sell")}
                data-testid="nav-post-listing-btn"
                className="rounded-none bg-safety hover:bg-safety-hover text-white"
              >
                <Plus className="w-4 h-4 mr-2" /> Post listing
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid="nav-user-menu" className="w-9 h-9 grid place-items-center bg-sage hover:bg-sage-dark transition-colors duration-200">
                    <User className="w-4 h-4 text-forest" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 rounded-none bg-white">
                  <DropdownMenuLabel className="font-normal">
                    <div className="font-medium">{user.name}</div>
                    <div className="text-xs text-muted-foreground truncate">{user.email}</div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate("/dashboard")} data-testid="menu-dashboard">Dashboard</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/dashboard/listings")} data-testid="menu-my-listings">My listings</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/dashboard/saved")} data-testid="menu-saved">Saved</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/dashboard/briefs")} data-testid="menu-briefs">My clearance requests</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/dashboard/messages")} data-testid="menu-messages">Messages</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/dashboard/subscriptions")} data-testid="menu-subscriptions">Subscriptions</DropdownMenuItem>
                  {(user.user_type === "business" || user.user_type === "removal_company") && (
                    <DropdownMenuItem onClick={() => navigate("/dashboard/payouts")} data-testid="menu-payouts">Payouts</DropdownMenuItem>
                  )}
                  {(user.user_type === "business" || user.user_type === "removal_company") && (
                    <DropdownMenuItem onClick={() => navigate("/dashboard/verification")} data-testid="menu-verification">
                      {user.is_verified ? "Verified ✓" : "Get verified"}
                    </DropdownMenuItem>
                  )}
                  {user.is_admin && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin" className="text-safety">
                        🛡️ Admin console
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={async () => { await logout(); navigate("/"); }}
                    data-testid="menu-logout"
                    className="text-safety"
                  >
                    <LogOut className="w-4 h-4 mr-2" /> Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <>
              <Button
                variant="ghost"
                onClick={() => navigate("/login")}
                data-testid="nav-login-btn"
                className="rounded-none"
              >
                Log in
              </Button>
              <Button
                onClick={() => navigate("/register")}
                data-testid="nav-register-btn"
                className="rounded-none bg-forest hover:bg-forest-800 text-white"
              >
                Join free
              </Button>
            </>
          )}
        </div>

        <Sheet>
          <SheetTrigger asChild>
            <button className="md:hidden" data-testid="nav-mobile-toggle" aria-label="Open menu">
              <Menu className="w-6 h-6" />
            </button>
          </SheetTrigger>
          <SheetContent side="right" className="w-72 bg-white">
            <div className="mt-8 flex flex-col gap-4">
              {links.map((l) => (
                <NavLink key={l.to} to={l.to} className="text-lg font-display">
                  {l.label}
                </NavLink>
              ))}
              <hr />
              {user ? (
                <>
                  <NavLink to="/dashboard" className="text-lg font-display">Dashboard</NavLink>
                  <NavLink to="/sell" className="text-lg font-display">Post listing</NavLink>
                  <button onClick={logout} className="text-lg font-display text-left text-safety">Log out</button>
                </>
              ) : (
                <>
                  <NavLink to="/login" className="text-lg font-display">Log in</NavLink>
                  <NavLink to="/register" className="text-lg font-display">Join free</NavLink>
                </>
              )}
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
}
